<script setup lang="ts">
import {
  GraphDataProviderColumn,
  GraphDataProviderQueryRoot,
  GraphDataProviderState,
  useContext,
  useEnums,
  useGraphDataProvider,
  useGraphMeta,
  usePhrases,
  useProcesses
} from "@hantera/portal-app";
import { DataTableContextMenuProvider, ListDetailLayout, RadioButton, TextInput } from "@hantera/design-system";
import { computed, ref, toRaw, watch } from "vue";
import useBackend from "@/composition/useBackend";
import InvoiceDetails from "@/views/Sales/InvoiceDetails.vue";
import useGraphPhrases from "@/composition/use-graph-phrases";
import useGraphAvailableFields from "@/composition/use-graph-available-fields";
import Decimal from "decimal.js";

const context = useContext()

const { graph, user } = useBackend()
const graphMeta = useGraphMeta()
const processes = useProcesses()
const phrases = usePhrases()
const enums = useEnums()
const graphPhrases = useGraphPhrases()
const { getAvailableFields } = useGraphAvailableFields()

context.currentView!.value!.name.value = 'Invoices'

const searchPhrase = ref('')

const stateFilter = ref('')

const query = computed<GraphDataProviderQueryRoot>(() => {

  let filter: string | undefined
  if (stateFilter.value === 'paid') {
    filter = 'invoiceTotal == capturedTotal'
  }
  if (stateFilter.value === 'unpaid') {
    filter = 'capturedTotal != invoiceTotal'
  }

  return {
    edge: 'invoices',
    dataFields: ['invoiceTotal', 'capturedTotal', 'invoiceId'],
    orderBy: 'createdAt desc',
    filter,
    phrase: searchPhrase.value,
    availableFields: getAvailableFields('invoice').map(c => ({
      field: c,
      label: graphPhrases.field('invoice', c)
    })),
    navigations: [{
      edge: 'order',
      dataFields: ['orderId'],
      availableFields: getAvailableFields('order').filter(c => c.indexOf('invoiceRecipient') === -1).map(c => ({
        field: c,
        label: graphPhrases.field('order', c)
      }))
    }]
  }
})

const defaultColumns = [{
  field: 'channelKey',
  queryPath: ['invoices', 'order'],
}, {
  field: 'invoiceNumber',
  queryPath: ['invoices'],
}, {
  field: 'createdAt',
  queryPath: ['invoices'],
}, {
  field: 'capturedTotal',
  queryPath: ['invoices'],
}, {
  field: 'invoiceTotal',
  queryPath: ['invoices'],
}, {
  field: 'currencyCode',
  queryPath: ['invoices', 'order'],
}, {
  field: 'orderNumber',
  queryPath: ['invoices', 'order'],
}]

const columns = ref<GraphDataProviderColumn[]>(
  user.value.settings['sales.invoices.columns'] ?? Array.from(defaultColumns)
)

watch(columns, () => {
  user.updateSettings({
    'sales.invoices.columns': toRaw(columns.value)
  })
}, {
  deep: true
})

const state = ref<GraphDataProviderState>(context.currentView.value!.state.listState ?? {
  filters: {}
})

watch(state, () => {
  context.currentView.value!.state.listState = state.value
}, { deep: true })

const live = ref<boolean>(context.currentView.value!.state.live ?? false)

watch(live, () => {
  context.currentView.value!.state.live = live.value
})

const selection = ref<unknown>(context.currentView.value!.state.selection)

watch(selection, () => {
  context.currentView.value!.state.selection = selection.value
})

const provider = useGraphDataProvider(query, columns, state, {
  live,
  rowHue(row): string | number | undefined {

    const capturedTotal = row['invoices.capturedTotal'] as Decimal
    const invoiceTotal = row['invoices.invoiceTotal'] as Decimal

    if (!capturedTotal.eq(invoiceTotal)) {
      return '--color-hue-red'
    }

    return '--color-hue-green'
  },
  rowKey(row) {
    return `${row['invoices.invoiceId']}@${row['invoices.order.orderId']}`
  }
})

const dataTableContextMenuProvider = computed<DataTableContextMenuProvider>(() => ({
  table: [[{
    label: 'Reset Layout',
    action: () => {
      columns.value = Array.from(defaultColumns)
      stateFilter.value = ''
    }
  }, {
    label: live.value ? 'Disable live updates' : 'Enable live updates',
    action() {
      live.value = !live.value
    }
  }, {
    label: 'Refresh',
    disabled: live.value,
    action() {
      provider.refresh()
    }
  }]]
}))

</script>

<template>
  <ListDetailLayout :provider="provider" :detail-component="InvoiceDetails" v-model:selection="selection"
    cache-key="sales_deliveries_layout" :data-table-context-menu-provider="dataTableContextMenuProvider">
    <template #list-toolbar>
      <div class="list-toolbar">
        <div class="state-selector">
          <RadioButton v-model="stateFilter" value="">All</RadioButton>
          <div class="separator"></div>
          <div class="btn-group">
            <RadioButton v-model="stateFilter" value="unpaid" class="unpaid">Unpaid</RadioButton>
            <RadioButton v-model="stateFilter" value="paid" class="paid">Paid</RadioButton>
          </div>
        </div>
        <div class="search">
          <TextInput type="search" v-model="searchPhrase" placeholder="Search" />
        </div>
      </div>
    </template>
  </ListDetailLayout>
</template>

<style scoped>
.list-toolbar {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 5px;
}

@container (max-width: 767px) {
  .list-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .search {
    width: 100%;
    order: 0;
  }

  .state-selector {
    order: 1;
  }
}

.state-selector {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 5px;
}

.separator {
  height: 100%;
  border-left: 1px solid hsl(var(--color-60));
}

.paid {
  --color-text: 0, 100%, 100%;
  --color-hue: var(--color-hue-green);
  --color-saturation: 71.7%;
  --color-lightness: 41.57%;
}

.unpaid {
  --color-text: 0, 100%, 100%;
  --color-hue: var(--color-hue-red);
  --color-saturation: 71.7%;
  --color-lightness: 41.57%;
}
</style>
